import type { MetaFunction } from "@remix-run/cloudflare";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { DownloadButton } from "~/components/ui/download-button";
import { ProtectedDownloadLink } from "~/components/ui/protected-download-link";
import { 
  Download, 
  Eye, 
  Heart, 
  Calendar,
  User,
  Shield,
  Star,
  FileText,
  BarChart3,
  MessageSquare,
  Clock,
  CheckCircle
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Improved Perfect Defense - Enhanced Combat Mechanics | stellarblademod.com" },
    { 
      name: "description", 
      content: "Improved Perfect Defense mod for Stellar Blade - Slightly increased perfect parry/dodge windows and decreased guard cooldown for better responsive gameplay." 
    },
    { name: "keywords", content: "stellar blade perfect defense, stellar blade combat mod, parry mod, dodge timing, guard cooldown, stellar blade gameplay" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/mods/improved-perfect-defense" },
    
    // Open Graph
    { property: "og:title", content: "Improved Perfect Defense - Enhanced Combat Mechanics" },
    { property: "og:description", content: "Improved Perfect Defense mod for Stellar Blade - Enhanced combat mechanics with better timing windows." },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://stellarblademod.com/mods/improved-perfect-defense" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Improved Perfect Defense - Enhanced Combat Mechanics" },
    { name: "twitter:description", content: "Improved Perfect Defense mod for Stellar Blade - Enhanced combat mechanics with better timing windows." },
  ];
};

export default function ImprovedPerfectDefense() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div 
        className="relative text-white py-12 border-b border-slate-800"
        style={{
          backgroundImage: "url('/mods/ImprovedPerfectDefense1.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mod Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30">
                  Miscellaneous
                </Badge>
                <Badge variant="secondary" className="bg-green-500/20 text-green-300 border-green-400/30">
                  Fair and balanced
                </Badge>
              </div>
              
              <h1 className="text-4xl font-bold mb-4">
                Improved Perfect Defense
              </h1>
              
              <p className="text-xl opacity-90 mb-6">
                Slightly increased perfect parry / dodge windows, and decreased guard cooldown / buffer. Multiple versions available for different skill levels and preferences.
              </p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Created by <a href="https://next.nexusmods.com/profile/andrewRCr?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Andrew Creekmore</a></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Updated: June 13, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Version 1.01</span>
                </div>
              </div>
            </div>
            
            {/* Stats Card */}
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 text-white backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-300">
                    <BarChart3 className="h-5 w-5" />
                    Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Endorsements
                    </span>
                    <span className="font-bold">540</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Downloads
                    </span>
                    <span className="font-bold">8,247</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Views
                    </span>
                    <span className="font-bold">15,632</span>
                  </div>
                  <Separator className="bg-slate-600" />
                  <DownloadButton className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg" scrollOnly>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </DownloadButton>
                  <p className="text-xs opacity-80 text-center">
                    Manual download available
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-cyan-300">About this mod</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none text-slate-300">
                <div className="space-y-6">
                  {/* Overview */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Overview:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        Increases the Perfect Parry and Perfect Dodge windows from <strong>9 frames → 12 frames</strong> (0.15s → 0.2s).
                      </p>
                      <p className="text-sm mb-3">
                        Also decreases the guard cooldown duration from <strong>24 frames → 21 frames</strong> (0.4s → 0.35s).
                      </p>
                      <p className="text-sm">
                        The guard cooldown duration governs the forced buffer between inputs. These changes to window and cooldown time are subtle, but feel noticeably better than vanilla, <strong>without</strong> trivializing the mechanic.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Features */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Key Features:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <Clock className="h-4 w-4 mt-1 text-blue-600" />
                        <span><strong>Enhanced Timing Windows:</strong> Perfect parry/dodge windows increased from 9 to 12 frames</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="h-4 w-4 mt-1 text-purple-600" />
                        <span><strong>Reduced Guard Cooldown:</strong> Faster recovery between defensive actions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-1 text-green-600" />
                        <span><strong>Multiple Versions:</strong> Standard and Extended versions available</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Star className="h-4 w-4 mt-1 text-yellow-600" />
                        <span><strong>Balanced Design:</strong> Enhances responsiveness without making the game too easy</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-4 w-4 mt-1 text-orange-600" />
                        <span><strong>Skill Compatibility:</strong> Stacks with existing timing enhancement skills and equipment</span>
                      </li>
                    </ul>
                  </div>

                  <Separator />

                  {/* Versions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Available Versions:</h3>
                    <div className="space-y-4">
                      <div className="bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-blue-300">Standard Version</h4>
                        <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                          <li>Perfect Parry/Dodge: 9 frames → 12 frames (0.15s → 0.2s)</li>
                          <li>Guard Cooldown: 24 frames → 21 frames (0.4s → 0.35s)</li>
                          <li>Recommended for most players seeking improved responsiveness</li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-900/20 border-l-4 border-green-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-green-300">Extended Version</h4>
                        <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                          <li>Perfect Parry/Dodge: 9 frames → 15 frames (0.15s → 0.25s)</li>
                          <li>Same guard cooldown improvements as standard</li>
                          <li>More forgiving timing for players who prefer larger windows</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Installation */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Installation Guide:</h3>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li><strong>Download</strong> your preferred version from the files tab</li>
                      <li><strong>Create a ~mods folder</strong> in your game's Paks directory: <code className="bg-slate-700 text-cyan-300 px-1 rounded">StellarBlade/SB/Content/Paks/~mods</code></li>
                      <li><strong>Extract</strong> the .pak, .utoc and .ucas files to the ~mods folder</li>
                      <li><strong>Launch the game</strong> and enjoy improved combat timing</li>
                    </ol>
                  </div>

                  <Separator />

                  {/* Compatibility */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Compatibility Notes:</h3>
                    <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        <strong>Skill/Gear Interactions:</strong> This mod is fully compatible with existing timing enhancement skills and equipment. All bonuses stack additively:
                      </p>
                      <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                        <li><strong>Focus Boost / Reflex Boost skills:</strong> +0.04s</li>
                        <li><strong>Reflex-Type Exospine:</strong> +0.04s</li>
                        <li><strong>Reflex-Type Exospine Mk. 2 (NG+):</strong> +0.06s</li>
                      </ul>
                    </div>
                  </div>

                  <Separator />

                  {/* Player Experience */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Player Experience:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        As a combat-focused enhancement, this mod strikes an excellent balance between accessibility and challenge. The timing improvements feel natural and responsive, making perfect parries and dodges more achievable without completely removing the skill requirement.
                      </p>
                      <p className="text-sm mb-3">
                        The reduced guard cooldown particularly shines in fast-paced encounters, allowing for more fluid defensive sequences. Players report feeling more confident in attempting reactive defensive play rather than relying solely on predictive timing.
                      </p>
                      <p className="text-sm">
                        This mod is especially valuable for players transitioning from other combat systems or those who want to experiment with different Exospine types beyond the Reflex-Type variants.
                      </p>
                    </div>
                  </div>

                  {/* Support Links */}
                  <div className="flex flex-wrap gap-4 pt-6">
                    <DownloadButton 
                      variant="outline" 
                      className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                      scrollOnly
                    >
                      <Download className="h-4 w-4" />
                      Download Latest Version
                    </DownloadButton>
                    <Button variant="outline" asChild>
                      <a href="/contact" className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800">
                        <MessageSquare className="h-4 w-4" />
                        Community Support
                      </a>
                    </Button>
                  </div>

                  <Separator />

                  {/* Original Page Link */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-slate-600 rounded-lg backdrop-blur-sm">
                    <h4 className="text-lg font-semibold mb-3 text-cyan-300">Visit Original Page</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Check the official Nexus Mods page for latest updates, community discussions, and technical support.
                    </p>
                    <Button asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 shadow-lg">
                      <a 
                        href="https://www.nexusmods.com/stellarblade/mods/232" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Star className="h-4 w-4" />
                        View Nexus Mods Page
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* File Information */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">File Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-slate-300">
                  <div className="flex justify-between text-sm">
                    <span>Last updated</span>
                    <span>June 13, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Original upload</span>
                    <span>June 12, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Created by</span>
                    <a href="https://next.nexusmods.com/profile/andrewRCr?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Andrew Creekmore</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Uploaded by</span>
                    <a href="https://next.nexusmods.com/profile/andrewRCr?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">andrewRCr</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Virus scan</span>
                    <Badge variant="secondary" className="bg-green-900/50 text-green-300 border-green-600">
                      Safe to use
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Download Section */}
              <Card id="download-section" className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Download Files</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Standard Version */}
                    <div className="space-y-4">
                      {/* File Information */}
                      <div className="p-4 border border-slate-600 rounded-lg">
                        <h4 className="font-semibold text-slate-200">Standard Version</h4>
                        <p className="text-sm text-slate-400">Balanced timing improvements</p>
                        <p className="text-xs text-slate-500">Version 1.01 • 2.3 MB</p>
                      </div>
                      
                      {/* Download Button */}
                      <div className="w-full">
                        <ProtectedDownloadLink 
                          href="https://r2.stellarblademod.com/Improved%20Perfect%20Defense-232-1-01-1749826636.zip" 
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </ProtectedDownloadLink>
                      </div>
                    </div>

                    {/* Extended Version */}
                    <div className="space-y-4">
                      {/* File Information */}
                      <div className="p-4 border border-slate-600 rounded-lg">
                        <h4 className="font-semibold text-slate-200">Extended Version</h4>
                        <p className="text-sm text-slate-400">Larger timing windows</p>
                        <p className="text-xs text-slate-500">Version 1.01 • 2.3 MB</p>
                      </div>
                      
                      {/* Download Button */}
                      <div className="w-full">
                        <ProtectedDownloadLink 
                          href="https://r2.stellarblademod.com/Improved%20Perfect%20Defense%20-%20Extended-232-1-01-1749826674.zip" 
                          variant="outline" 
                          className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </ProtectedDownloadLink>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-slate-600 text-slate-300">combat</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">timing</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">parry</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">dodge</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">balanced</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Permissions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-slate-400">
                    <p><strong className="text-slate-300">Upload permission:</strong> Not allowed</p>
                    <p><strong className="text-slate-300">Modification permission:</strong> Permission required</p>
                    <p><strong className="text-slate-300">Conversion permission:</strong> Not allowed</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 